import React, { useEffect, useState } from "react";
import { SimpleTable } from "../../ui/SimpleTable/SimpleTable";
import { ConfirmDialog } from "../../ConfirmDialog/ConfirmDialog";
import { Container } from "../../../../../types/api/guide/container.types";
import { containerService } from "../../../../../services/guide/container-service";
import useAppSnackbar from "../../../../../hooks/useAppSnackbar";
import { useFetchPagedContainer } from "../../../../../hooks/guide/useFetchPagedContainer";
import { useDebouncedValue } from "../../../../../hooks/useDebouncedValue";
import { PagedResultDto } from "../../../../../types/api/PagedResultDto";

interface ContainersTableProps {
    openEdit?: (row: any) => void;
    canEdit?: boolean;
    title: string;
}

export const ContainersTable = ({ title, openEdit, canEdit }: ContainersTableProps) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 350);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const {
        data: paged,
        isLoading,
        error,
        refetch,
    } = useFetchPagedContainer(page, pageSize, debouncedSearch.trim() || undefined);

    const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();
    const [toDelete, setToDelete] = useState<Container | null>(null);

    const pageData = paged as PagedResultDto<Container> | null | undefined;
    const items = pageData?.items ?? [];
    const totalCount = pageData?.totalCount ?? 0;

    const handleDelete = async (id: string) => {
        try {
            await containerService.delete(id);
            showSuccess("Успешное удаление");
            refetch();
        } catch (e: any) {
            showError(e.response?.data);
            console.log("Ошибка при удалении контейнера");
        }
    };

    return (
        <>
            <SimpleTable<Container>
                title={title}
                columns={[
                    { key: "containerPlatformName", title: "Название КП" },
                    { key: "kind", title: "Вид" },
                    { key: "typeContainer", title: "Тип контейнера" },
                    {
                        key: "volume",
                        title: "Объем",
                        render: (r) => Number(r.volume).toFixed(2),
                    },
                ]}
                data={items}
                loading={isLoading}
                error={error}
                enableSearchByName
                searchPlaceholder="Поиск по названию КП"
                serverPagination={{
                    totalCount,
                    page,
                    pageSize,
                    onPageChange: setPage,
                    onPageSizeChange: (s) => {
                        setPageSize(s);
                        setPage(1);
                    },
                    searchValue: search,
                    onSearchChange: setSearch,
                }}
                getRowKey={(r) => r.id}
                getRowActions={(row) => ({
                    onEdit: canEdit && openEdit ? () => openEdit(row) : undefined,
                })}
                isNeedAdd={false}
                emptyText="Справочник пуст"
            />

            <ConfirmDialog
                open={!!toDelete}
                title="Удалить контейнер?"
                description={
                    toDelete
                        ? `Будет удалён из: ${toDelete.typeContainer} / ${toDelete.typeContainer}`
                        : undefined
                }
                onCancel={() => setToDelete(null)}
                onConfirm={() => {
                    if (!toDelete) return;
                    handleDelete(toDelete.id);
                    setToDelete(null);
                }}
            />
            <SnackbarComponent />
        </>
    );
};
